import dayjs from 'dayjs';
import { deptOf, isPaused, isWorking, leaveBalance, nextCode, positionById, toDay, type Employee, type HrState } from './hrLogic';
import { DEFAULT_TIME_SETTINGS, SHIFT_COLORS, holidayOf, leaveOn, monthDays, monthOf, shiftAt, stdDays, weekStart, type Assign, type ShiftDef, type TimeReq, type TpState } from './tpLogic';
import { DEFAULT_PAY_SETTINGS, calcPeriodRows, type Adjust } from './payLogic';

/** Tăng số này khi đổi cấu trúc dữ liệu demo chấm công – lương */
export const TP_DEMO_VERSION = 2;

function rng(seed: number) {
  let x = seed | 0;
  return () => {
    x = (x + 0x6d2b79f5) | 0;
    let t = Math.imul(x ^ (x >>> 15), 1 | x);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const DEFAULT_SHIFTS = (clinics: string[], office: string[]): ShiftDef[] => [
  { id: 'sh-hc', code: 'HC', name: 'Hành chính', start: '08:00', end: '17:00', breakMin: 60, work: 1, color: SHIFT_COLORS[5], branchIds: office, active: true },
  { id: 'sh-s', code: 'S', name: 'Ca sáng', start: '09:00', end: '18:00', breakMin: 60, work: 1, color: SHIFT_COLORS[0], branchIds: clinics, minStaff: 4, needDoctor: true, active: true },
  { id: 'sh-c', code: 'C', name: 'Ca chiều', start: '12:00', end: '21:00', breakMin: 60, work: 1, color: SHIFT_COLORS[1], branchIds: clinics, minStaff: 4, needDoctor: true, active: true },
  { id: 'sh-d', code: 'Đ', name: 'Trực đêm hậu phẫu', start: '21:00', end: '07:00', breakMin: 60, work: 1, color: SHIFT_COLORS[2], branchIds: clinics, active: true },
  { id: 'sh-n', code: 'N', name: 'Nửa ca sáng', start: '08:00', end: '12:00', breakMin: 0, work: 0.5, color: SHIFT_COLORS[3], branchIds: [], active: true },
];

/** Lịch ca, giờ chấm công, đơn từ tháng trước → hết tuần sau; tháng trước đã chốt công và có bảng lương chờ duyệt */
export function tpDemoState(hr: HrState, now = Date.now(), by = 'Phòng Nhân sự'): TpState {
  const rnd = rng((hr.seededAt % 1000003) + 17);
  const ri = (a: number, b: number) => a + Math.floor(rnd() * (b - a + 1));
  const today = toDay(now);
  const thisMonth = monthOf(today);
  const prevMonth = dayjs(now).subtract(1, 'month').format('YYYY-MM');
  const from = `${prevMonth}-01`;
  const to = dayjs(weekStart(today)).add(13, 'day').format('YYYY-MM-DD');
  const clinics = hr.branches.filter(b => b.clinic).map(b => b.id);
  const office = hr.branches.filter(b => !b.clinic).map(b => b.id);
  const shifts = DEFAULT_SHIFTS(clinics, office);
  const time = DEFAULT_TIME_SETTINGS;
  const sh = (id: string) => shifts.find(x => x.id === id)!;

  /* ----- Xếp ca theo nếp: văn phòng & quản lý làm hành chính T2–T7, chi nhánh xoay ca sáng / chiều, nghỉ 1 ngày trong tuần ----- */
  const assigns: Assign[] = [];
  const emps = hr.employees.filter(e => e.hireDate <= to && (!e.leftDate || e.leftDate >= from));
  const byBranch: Record<string, Employee[]> = {};
  emps.forEach(e => { (byBranch[e.branchId] = byBranch[e.branchId] || []).push(e); });
  Object.values(byBranch).forEach(l => l.sort((a, b) => a.code.localeCompare(b.code)));
  const w0 = dayjs(weekStart(from));
  // Không xếp quá công chuẩn của tháng (ngày lễ đã được tính công)
  const cap = (m: string) => stdDays(time, m) - monthDays(m).filter(d => holidayOf(time, d) && !time.weeklyOff.includes(dayjs(d).day())).length;
  const count = new Map<string, number>();
  for (let d = dayjs(from); !d.isAfter(dayjs(to), 'day'); d = d.add(1, 'day')) {
    const date = d.format('YYYY-MM-DD');
    if (holidayOf(time, date)) continue;
    const wk = Math.floor(d.diff(w0, 'day') / 7);
    const wd = d.day();
    Object.entries(byBranch).forEach(([br, list]) => {
      const clinic = clinics.includes(br);
      const docs = list.filter(e => deptOf(hr, e) === 'doctor' && positionById(hr, e.positionId)?.level === 'staff');
      list.forEach((e, idx) => {
        if (!isWorking(e, date) || isPaused(e, date)) return;
        if ((leaveOn(e, date)?.part || 0) >= 1) {
          if (wd !== 0) count.set(`${e.id}|${monthOf(date)}`, (count.get(`${e.id}|${monthOf(date)}`) || 0) + 1);
          return;
        }
        const lvl = positionById(hr, e.positionId)?.level;
        let shiftId: string | null = null;
        if (!clinic) shiftId = wd !== 0 ? 'sh-hc' : null;
        else if (lvl === 'manager' || lvl === 'director') shiftId = wd !== 0 ? 'sh-s' : null;
        else if (deptOf(hr, e) === 'doctor') {
          const j = docs.indexOf(e);
          if (wd !== (j * 2 + 1) % 7) shiftId = docs.length === 1 || (wk + j) % 2 ? 'sh-c' : 'sh-s';
        } else if (wd !== (idx % 6) + 1) shiftId = (wk + idx) % 2 ? 'sh-c' : 'sh-s';
        if (!shiftId) return;
        const k = `${e.id}|${monthOf(date)}`;
        if ((count.get(k) || 0) >= cap(monthOf(date))) return;
        count.set(k, (count.get(k) || 0) + 1);
        assigns.push({ id: `a${(assigns.length + 1).toString(36)}`, empId: e.id, date, shiftId, branchId: br });
      });
    });
  }

  /* ----- Giờ chấm công + đơn giải trình ----- */
  const requests: TimeReq[] = [];
  const empName = (id: string) => hr.employees.find(e => e.id === id)?.name || by;
  const lead = 'Trưởng chi nhánh';
  const addReq = (r: Omit<TimeReq, 'id' | 'code'>) => {
    requests.push({ ...r, id: `rq${(requests.length + 1).toString(36)}`, code: nextCode(requests.map(x => x.code), 'DT', r.date || r.from!) });
  };
  assigns.forEach(a => {
    const s = sh(a.shiftId);
    const { startAt, endAt } = shiftAt(a.date, s);
    if (startAt > now) return;
    const prev = monthOf(a.date) === prevMonth;
    const x = rnd();
    const inN = startAt - ri(2, 20) * 60000;
    const outN = endAt + ri(0, 25) * 60000;
    let inAt: number | undefined = inN;
    let outAt: number | undefined = outN;
    if (x < 0.07) inAt = startAt + ri(6, 35) * 60000;
    else if (x < 0.09) outAt = endAt - ri(10, 40) * 60000;
    else if (x < 0.11 && outN < now) {
      outAt = undefined;
      const ask = { empId: a.empId, kind: 'explain' as const, date: a.date, assignId: a.id, outTime: dayjs(outN).format('HH:mm'), reason: 'Quên chấm công khi ra về', by: empName(a.empId), at: endAt + 14 * 3600000 };
      if (prev) { outAt = outN; a.src = 'manual'; addReq({ ...ask, status: 'approved', decidedBy: lead, decidedAt: endAt + 30 * 3600000 }); }
      else if (rnd() < 0.5 && ask.at < now) addReq({ ...ask, status: 'pending' });
    } else if (x < 0.117 && !prev && a.date < today) { inAt = undefined; outAt = undefined; }
    if (a.date === today && rnd() < 0.08) { inAt = undefined; outAt = undefined; }
    if (inAt && inAt > now) inAt = undefined;
    if (outAt && outAt > now) outAt = undefined;
    a.inAt = inAt; a.outAt = outAt;
    if (inAt || outAt) a.src = a.src || 'device';
  });

  /* ----- Đơn nghỉ: đợt nghỉ đã có trong hồ sơ = đơn đã duyệt; thêm vài đơn chờ duyệt / bị từ chối ----- */
  emps.forEach(e => (e.leaveRecords || []).filter(r => r.to >= from).forEach(r => {
    addReq({ empId: e.id, kind: 'leave', leaveKind: r.kind, from: r.from, to: r.to, days: r.days, reason: r.reason || 'Nghỉ phép', by: e.name, at: r.at, status: 'approved', decidedBy: r.by, decidedAt: r.at, leaveId: r.id });
  }));
  const clinicStaff = emps.filter(e => clinics.includes(e.branchId) && isWorking(e, today) && positionById(hr, e.positionId)?.level === 'staff' && deptOf(hr, e) !== 'doctor');
  const pickStaff = () => clinicStaff[ri(0, clinicStaff.length - 1)];
  const nextWorkday = (n: number) => { let d = dayjs(today).add(n, 'day'); while (d.day() === 0) d = d.add(1, 'day'); return d.format('YYYY-MM-DD'); };
  const used = new Set<string>();
  for (let i = 0; i < 3 && clinicStaff.length; i += 1) {
    const e = pickStaff();
    if (used.has(e.id) || leaveBalance(hr, e, today).remain < 2) continue;
    used.add(e.id);
    const d = nextWorkday(3 + i * 2);
    if (leaveOn(e, d)) continue;
    addReq({ empId: e.id, kind: 'leave', leaveKind: i === 2 ? 'personal' : 'annual', from: d, to: d, days: 1, reason: ['Về quê có việc gia đình', 'Đi khám sức khỏe', 'Đám cưới em gái'][i], by: e.name, at: now - ri(2, 30) * 3600000, status: 'pending' });
  }
  if (clinicStaff.length) {
    const e = pickStaff();
    const d = dayjs(`${thisMonth}-06`).format('YYYY-MM-DD');
    if (d < today) addReq({ empId: e.id, kind: 'leave', leaveKind: 'personal', from: d, to: d, days: 1, reason: 'Việc gia đình', by: e.name, at: dayjs(d).subtract(2, 'day').valueOf(), status: 'rejected', decidedBy: lead, decidedAt: dayjs(d).subtract(1, 'day').valueOf(), decideNote: 'Trùng lịch khách cao điểm, đề nghị đổi sang ngày khác' });
  }

  /* ----- Làm thêm giờ sau ca chiều (21:00 – 23:00) ----- */
  const evening = assigns.filter(a => a.shiftId === 'sh-c' && a.inAt && a.outAt && clinics.includes(a.branchId));
  const otFor = (a: Assign, status: TimeReq['status']) => addReq({
    empId: a.empId, kind: 'ot', date: a.date, otFrom: '21:00', otTo: '23:00', hours: 2, nightHours: 1, reason: 'Khách làm thủ thuật kéo dài sau giờ', by: empName(a.empId),
    at: shiftAt(a.date, sh('sh-c')).startAt, status, ...(status === 'approved' ? { decidedBy: lead, decidedAt: shiftAt(a.date, sh('sh-c')).endAt + 3 * 3600000 } : {}),
  });
  const prevEve = evening.filter(a => monthOf(a.date) === prevMonth);
  const curEve = evening.filter(a => monthOf(a.date) === thisMonth && a.date < today);
  for (let i = 0; i < 8 && prevEve.length; i += 1) otFor(prevEve[ri(0, prevEve.length - 1)], 'approved');
  for (let i = 0; i < 4 && curEve.length; i += 1) otFor(curEve[ri(0, curEve.length - 1)], 'approved');
  for (let i = 0; i < 2 && curEve.length; i += 1) otFor(curEve[ri(0, curEve.length - 1)], 'pending');

  /* ----- Đổi ca chờ duyệt ----- */
  const future = assigns.filter(a => a.date > today && clinics.includes(a.branchId) && clinicStaff.some(e => e.id === a.empId));
  for (let i = 0; i < 20 && future.length; i += 1) {
    const a = future[ri(0, future.length - 1)];
    const me = hr.employees.find(e => e.id === a.empId)!;
    const mate = clinicStaff.find(e => e.id !== me.id && e.branchId === me.branchId && deptOf(hr, e) === deptOf(hr, me) && !assigns.some(x => x.empId === e.id && x.date === a.date));
    if (!mate) continue;
    addReq({ empId: me.id, kind: 'swap', date: a.date, assignId: a.id, toEmpId: mate.id, reason: `Có việc riêng, ${mate.name} đồng ý làm thay`, by: me.name, at: now - 5 * 3600000, status: 'pending' });
    break;
  }

  /* ----- Khoản điều chỉnh ----- */
  const adjustments: Adjust[] = [];
  const addAdj = (a: Omit<Adjust, 'id'>) => adjustments.push({ ...a, id: `adj${(adjustments.length + 1).toString(36)}` });
  emps.filter(e => deptOf(hr, e) === 'consult' && isWorking(e, `${prevMonth}-15`)).forEach(e => {
    addAdj({ empId: e.id, kind: 'commission', month: prevMonth, amount: ri(15, 60) * 100000, note: `Hoa hồng tư vấn ${dayjs(from).format('MM/YYYY')}`, by, at: dayjs(`${thisMonth}-02`).valueOf() });
  });
  const active = emps.filter(e => isWorking(e, today) && clinics.includes(e.branchId));
  for (let i = 0; i < 3 && active.length; i += 1) {
    const e = active[ri(0, active.length - 1)];
    addAdj({ empId: e.id, kind: 'advance', month: prevMonth, amount: ri(2, 3) * 1000000, note: 'Tạm ứng giữa tháng', by, at: dayjs(`${prevMonth}-15`).valueOf() });
    const f = active[ri(0, active.length - 1)];
    addAdj({ empId: f.id, kind: 'advance', month: thisMonth, amount: ri(2, 3) * 1000000, note: 'Tạm ứng giữa tháng', by, at: now - ri(1, 5) * 86400000 });
  }

  const t: TpState = {
    v: 1, seededAt: now, demoVersion: TP_DEMO_VERSION, hrSeededAt: hr.seededAt,
    shifts, assigns, requests,
    locks: Array.from(new Set(emps.map(e => e.branchId))).map(branchId => ({ month: prevMonth, branchId, at: dayjs(`${thisMonth}-03T17:00`).valueOf(), by })),
    published: Object.keys(byBranch).flatMap(branchId => {
      const out: TpState['published'] = [];
      for (let w = dayjs(weekStart(from)); !w.isAfter(dayjs(weekStart(today))); w = w.add(7, 'day')) out.push({ branchId, week: w.format('YYYY-MM-DD'), at: w.subtract(3, 'day').hour(17).valueOf(), by });
      return out;
    }),
    time, pay: DEFAULT_PAY_SETTINGS,
    periods: [], adjustments, log: [],
  };
  const calcAt = dayjs(`${thisMonth}-05T10:00`).valueOf();
  t.periods = [{
    id: 'pp-demo', month: prevMonth, status: 'pending', rows: calcPeriodRows(hr, t, prevMonth, now), calcAt, calcBy: by,
    submittedAt: calcAt + 3600000, submittedBy: by, log: [{ at: calcAt, by, text: 'Tạo kỳ lương' }, { at: calcAt + 3600000, by, text: 'Gửi duyệt' }],
  }];
  return t;
}
